import React from 'react';
import { BookOpen, Volume2, Info, ChevronRight, Home, Castle, MapPin } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface RobertsPart1SectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const RobertsPart1Section: React.FC<RobertsPart1SectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 text-[#2c221e] font-sans">
      {/* Top Breadcrumb / Page Header */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold">
            {onSelectPage && (
              <button
                onClick={() => onSelectPage('hub')}
                className="hover:underline flex items-center gap-1 text-[#7a5b3a] cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                <span>The Roberts Family</span>
              </button>
            )}
            {onSelectPage && <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />}
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full uppercase tracking-wider text-[11px] border border-[#d8ceb9]">
              Part 1 of 5
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={speakCurrentPage}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
              title="Listen to Part 1: The Roberts Family in Wales"
            >
              <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
              <span>{isPlaying ? 'Listening to Part 1...' : 'Listen to Part 1'}</span>
            </button>
          </div>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          THE ROBERTS FAMILY IN WALES
        </h1>
        <p className="font-serif text-base sm:text-lg text-[#7a5b3a] italic leading-relaxed">
          Welsh heritage at Penmaenmawr, patronymic traditions, friendship with Prime Minister Gladstone, and pioneering life at Skylight, Cabbage Tree Flat.
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Intro sentence */}
        <p className="text-lg font-serif italic text-[#4a3525] bg-[#f3ede0] p-4 sm:p-5 rounded-2xl border-l-4 border-[#7a5b3a]">
          They would become linked to the Dentons when Lizzie Roberts married Jim Gibb and their second daughter, Annie, married Tommy Denton in 1919.
        </p>

        {/* Section: Gladstone Thanked Edward and Mary Roberts */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight border-b-2 border-[#d8ceb9] pb-2">
            GLADSTONE THANKED EDWARD AND MARY ROBERTS FOR LOYAL SUPPORT
          </h2>

          <p>
            The Roberts family in Wales were supporters of the Liberal Party, led for decades by Scots-born politician William Gladstone, who served 12 years as British Prime Minister, over four terms. Gladstone's Welsh estate, gained through his wife Catherine Glynne's family, was Hawarden Castle, near Chester in North Wales, near Penmaenmawr where the Roberts family lived at Plas Isa and Lonfa. Gladstone was often in Wales, where the Liberal Party won 29 of the 33 Welsh seats in 1885. They would have known each other over many years. Like many in non-conformist Wales, the Roberts family supported the liberal policies of limited government spending, low taxes, balanced budgets and an emphasis on self-help, freedom of choice and free trade.
          </p>

          <p>
            After the election of 1866 Gladstone thanked Edward and Mary Roberts for their loyal support over many years by inviting them as his house guest at Hawarden Castle, as it was below in the 1880s.
          </p>

          {/* Image 171 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/171 Edward Roberts 1813 - 1898.jpg"
              alt="Edward Roberts (1813-1898)"
              caption="Edward Roberts (1813-1898)"
              filenameHint="171 Edward Roberts 1813 - 1898.jpg"
            />
          </figure>

          <p>
            Edward, as a successful businessman, also took part in community and political matters. In the 1860's he served as an elected foundation member of the Penmaenmawr Board of Health and was appointed Inspector of Nuisances and Surveyor of Highways. He was a member and deacon of the local Presbyterian Church, a member of the Widow and Orphan Protection Lodge, and a member of the Nottingham Imperial Order of Oddfellows (a fraternal and charitable organization).
          </p>

          <p>
            He was Pencenedl or Chief of Kindred of the House of Roberts. That title passed to his second son Thomas in 1898 and then to Thomas Edward Lloyd Roberts (the poet T.E.L. Roberts) in 1924.
          </p>

          {/* Image 172 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/172 Eliza Parbery 1817-62.jpg"
              alt="Edward's first wife, Eliza, nee Parbery (1817-1862) died of cancer"
              caption="Edward's first wife, Eliza, nee Parbery (1817-1862) died of cancer"
              filenameHint="172 Eliza Parbery 1817-62.jpg"
            />
          </figure>

          <p>
            After Eliza died Edward married Mary Boynton of Cwmlys in July 1863, nearly 10 months later. Mary died in 1910, 12 years after Edward died and was buried in the Dwyfiyfylchi Churchyard.
          </p>

          <p>
            Edward and Eliza's son Robert Morris Roberts arrived in New Zealand in 1862, the same year his mother died.
          </p>

          {/* Image 173 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/173 Edward Roberts of Lonfa.jpg"
              alt="Edward Roberts of Lonfa"
              caption="Edward Roberts of Lonfa"
              filenameHint="173 Edward Roberts of Lonfa.jpg"
            />
          </figure>

          {/* Image 174 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/174 Penmaenmawr 1890.jpg"
              alt="Penmaenmawr 1890"
              caption="Penmaenmawr 1890"
              filenameHint="174 Penmaenmawr 1890.jpg"
            />
          </figure>
        </section>

        {/* Section: Welsh Naming Traditions */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight border-b-2 border-[#d8ceb9] pb-2">
            THE ROBERTS WERE CALLED JONES AND WILLIAMS....AND THEN ROBERTS
          </h2>

          <p>
            William Jones of Gwddw Glas (Green Gorge) and his family were living there at the turn of the 1700's. His parents married in 1675, John Jones and Jane Thomas. At that time the Welsh didn't have surnames - they took the father's first name to be the surname, so each generation had a new surname!
          </p>

          <p>
            Robert Williams was the son of William Jones so he became Williams of Bryn Mor (hill above the sea) further out on the Penmaenmawr peninsula. He drowned in 1763. Following tradition, the next generation became Roberts, and Edward Roberts married Jane Lloyd.
          </p>

          <p>
            In another complication, colloquially known by their properties, so that Robert Roberts of Bicht (the son of Edward Roberts) was known locally as Robin Beck, and Edward Roberts of Lonfa was called Ted Lonfa.
          </p>

          <p>
            Earlier in the family history there had been a property called Ty Mawr (big house) but it has long gone. It belonged to the Archbishop Williams of York at the time of the Civil War<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-roberts-1">1</a></sup> and had also belonged to the Cotymore family - that name translates as "a small wood" and William Jones had married Grace Smallwood.
          </p>

          {/* Image 175 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/175 Jane and Robert Roberts, known as Robin Beck.jpg"
              alt="Jane and Robert Roberts (called Robin Beck)"
              caption="Jane and Robert Roberts (called Robin Beck). They had a small farm and he also worked in the local quarry. His suit was made from material spun and woven by Jane, shown with knitting in progress."
              filenameHint="175 Jane and Robert Roberts, known as Robin Beck.jpg"
            />
          </figure>

          {/* Image 176 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/176 Tregarnedd home to the Roberts family 1781-95.jpg"
              alt="Tregarnedd"
              caption="Tregarnedd (dwelling by the cairn), home to the Roberts 1781-95 (later demolished). (Photo: Ailsa Fraser)"
              filenameHint="176 Tregarnedd home to the Roberts family 1781-95.jpg"
            />
          </figure>

          {/* Image 177 (Roberts Family Tree) */}
          <div className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/177 Roberts family tree.png"
              alt="Roberts family tree"
              caption="Roberts family tree"
              filenameHint="177 Roberts family tree.png"
            />
          </div>
        </section>

        {/* Section: Welsh Houses */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight border-b-2 border-[#d8ceb9] pb-2">
            SOME HOUSES LIVED IN BY MEMBERS OF THE ROBERTS FAMILY IN WALES
          </h2>

          {/* Image 178 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/178 Bicht in 1872.jpg"
              alt="Bicht in 1872"
              caption="Bicht in 1872 (Photo: Ailsa Fraser)"
              filenameHint="178 Bicht in 1872.jpg"
            />
          </figure>

          {/* Image 179 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/179 Lonfa.jpg"
              alt="Lonfa, Penmaenmawr"
              caption="Lonfa, Penmaenmawr (now named Plas Heulog meaning Sunny Place) has lovely marble fireplaces, wooden staircases and on a fine day, gives views of the Irish Sea."
              filenameHint="179 Lonfa.jpg"
            />
          </figure>

          {/* Image 180 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/180.jpg"
              alt="Plas Isa"
              caption="Plas Isa - built in 1856 by Edward Roberts at Penmaenmawr, across Mountain Lane from Lonfa. It became a guest house in which Edward retained an interest."
              filenameHint="180.jpg"
            />
          </figure>

          {/* Image 181 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/181 Skylight at Greta valley where Robert and sarah moved to in 1878.jpg"
              alt="Skylight at Greta valley"
              caption="And in New Zealand...... they called it Skylight. Robert Morris and Sarah Elizabeth Roberts outside their house at Cabbage Tree Flat. Their son Edward was born in this house in 1878."
              filenameHint="181 Skylight at Greta valley where Robert and sarah moved to in 1878.jpg"
            />
          </figure>
        </section>

        {/* Section: Edward Roberts: A Personal History */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            Edward Roberts: A Personal History<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-roberts-2">2</a></sup>
          </h2>

          <div className="space-y-5 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "Our first home at Cabbage Tree Flat was a sod one with a thatched roof and sod chimney. George Arkle, Jack Bunting and father<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-roberts-3">3</a></sup> built it. The rafters were obtained from native bush in the first gully over the Motunau hill. The chimney got on fire one morning in 1883 and spread to the thatch, and soon all inside the house was destroyed.
            </p>

            <p>
              Mr and Mrs Bennet took Baxter and Margaret and the other members of the family were taken in by Mrs Gibb. Someone started a subscription list and Mrs Gibb was one of the first subscribers with a donation of 2 Pounds. She also asked and got a donation from Mr George, later Sir George Clifford, owner of Stoneyhurst Sheep Station. Father owed Mr Sanderson of Greta Peaks for mutton and received a receipt from him stating payment had been received. Friends at Sefton also subscribed generously, considering the hard times, and the final list amounted to 60 Pounds.
            </p>

            <p>
              Father and a carpenter named Charley Horniblow built a three-room wooden house, with a loft in it. The northern end of the loft was used as a boys' bedroom. There was no match lining under the iron, and not much space between the floor and roof.
            </p>

            <p>
              As there was little shelter the house used to shake in the nor-west winds Between the top of the stairs and bedroom there was a small department used for storing odds and ends.... A kitchen with colonial oven, and two bedrooms were on the ground floor - the rooms all in a row with the kitchen on the east end. The chimney was made of sheets of corrugated iron. There was a lean-to on the back used as a dairy. None of the rooms were match lined before 1890 but ordinary newspaper covered the weatherboards to prevent draught. Early in 1890 father match lined the walls of the kitchen to a height of about 4 feet and scrim and papered above the woodwork."
            </p>
          </div>

          <p>
            That farm was sold in March 1890 to William Acton-Adams for 300 Pounds, with six months to shift. They moved to Happy Valley<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-roberts-4">4</a></sup> to clear 30 ha of gorse and manuka. Just before that shift they had bought the 150-ha land in the Waikari Valley. A local farmer leased it, until he ran into financial trouble and went to Australia leaving the rent debt behind!
          </p>

          <p>
            The house at Happy Valley had sod walls, the rafters were from the bush and the roof was corrugated iron. There was a very small kitchen with an earth floor, a front room and a bedroom. Another bedroom was added on the west end of the house for the girls. The inner wall as match lining and the outside wall was made of cob held in place by wooden laths. The children helped mix it by tramping it in with their bare feet.
          </p>

          <p>
            Their first Christmas day at Happy Valley was spent at a picnic on Motunau Beach, travelling to and from with a horse and dray, and sharing the day with six other people.
          </p>
        </section>

        {/* Footnotes Section */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl">
          <div className="flex items-center gap-2 text-[#7a5b3a] font-serif font-bold text-sm uppercase tracking-wider">
            <Info className="w-4 h-4" />
            <span>Footnotes</span>
          </div>

          <ol className="space-y-2 text-xs sm:text-sm text-[#4a3525] leading-relaxed list-decimal list-inside pl-1">
            <li id="fn-roberts-1" className="scroll-mt-24">
              1642-1651
            </li>
            <li id="fn-roberts-2" className="scroll-mt-24">
              Page 8
            </li>
            <li id="fn-roberts-3" className="scroll-mt-24">
              Arkle was a local, Jack Bunting was Sarah's stepbrother and father was Robert Morris Roberts
            </li>
            <li id="fn-roberts-4" className="scroll-mt-24">
              The land belonged to Bill Tully who gave them 4 years rent free to clear the farm and then 2 years rental. Tully was working as married couple on Mount Vulcan for the Reece family.
            </li>
          </ol>
        </section>

        {/* Navigation Footer */}
        {onSelectPage && (
          <div className="pt-6 border-t border-[#d8ceb9] flex flex-wrap items-center justify-between gap-4">
            <button
              onClick={() => onSelectPage('hub')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-sm transition cursor-pointer"
            >
              <Home className="w-4 h-4" />
              <span>Back to Roberts Family Hub</span>
            </button>

            <button
              onClick={() => onSelectPage('part2')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition shadow-xs cursor-pointer"
            >
              <span>Continue to Part 2 &rarr;</span>
            </button>
          </div>
        )}

      </article>
    </div>
  );
};
